import { useState } from 'react';
import { FormField, FormSheet, Input, Select } from '@/shared/components';
import type { ResourceBlockReason, ResourceOccupancyRow } from '@/shared/api';
import { SLOT_MINUTES } from '../resourceModel';
import { BLOCK_REASON_LABEL } from './resourceCalendarModel';
import { ResourcePickField } from './ResourcePickField';
import { ResourceTimeFields } from './ResourceTimeFields';
import { fitWindow, sheetDay } from './resourceBlockModel';
import { timeZoneWord } from '@/shared/lib/timeZoneWord';

/**
 * Sperrt einen Zeitraum eines Tages; der Server prüft Fenster, Überschneidung und Zeitumstellung.
 * Eine im Raster gezogene Zeit kommt vorbelegt an; ohne sie beginnt das Blatt beim Öffnen der
 * Ressource. Gesendet wird genau die Zeit, die das Blatt zeigt – auch nach einem Wechsel der
 * Ressource oder wenn ihr Fenster sich beim Neuladen verschoben hat.
 */
export function ResourceBlockSheet({
  rows,
  resourceId,
  initialStart,
  initialEnd,
  date,
  dates,
  timezone,
  saving,
  error,
  onSubmit,
  onClose,
}: {
  /** Die Ressourcen, die sich sperren lassen; bei mehreren gibt es ein Feld zur Wahl. */
  rows: ResourceOccupancyRow[];
  resourceId: string;
  initialStart?: number;
  initialEnd?: number;
  date: string;
  dates: string[];
  timezone: string;
  saving: boolean;
  error: string | null;
  onSubmit: (value: {
    date: string;
    startMinute: number;
    endMinute: number;
    reason: ResourceBlockReason;
    note: string | null;
    resourceId: string;
  }) => void;
  onClose: () => void;
}) {
  // Reihenfolge der Felder wie vor dem Raster: die Nutzlast bleibt byte-gleich.
  const [initial] = useState(() => {
    const start =
      initialStart ?? rows.find((item) => item.resourceId === resourceId)?.opensAtMinute ?? 0;
    return {
      date,
      startMinute: start,
      endMinute: initialEnd ?? start + SLOT_MINUTES,
      reason: 'Cleaning' as ResourceBlockReason,
      note: '',
      resourceId,
    };
  });
  const [value, setValue] = useState(initial);
  const row = rows.find((item) => item.resourceId === value.resourceId);
  const time = row ? fitWindow(value.startMinute, value.endMinute, row) : value;
  const patch = (next: Partial<typeof value>) =>
    setValue((current) => {
      const merged = { ...current, ...next };
      return merged.endMinute <= merged.startMinute
        ? { ...merged, endMinute: merged.startMinute + SLOT_MINUTES }
        : merged;
    });
  // Nie still auf eine andere Ressource ausweichen: ist die gewählte weg, sagt das Blatt es.
  const gone = row
    ? null
    : rows.length > 0
      ? 'Die gewählte Ressource ist nicht mehr aktiv. Wähle eine andere.'
      : 'Keine Ressource ist mehr aktiv – es lässt sich nichts sperren.';

  return (
    <FormSheet
      title={rows.length > 1 || !row ? 'Zeit sperren' : `${row.name}: Zeit sperren`}
      description={`Für Reinigung, Eigenbedarf oder Reparatur. Zeitzone: ${timeZoneWord(timezone)}; der Gast sieht nur „belegt".`}
      dirty={JSON.stringify(value) !== JSON.stringify(initial)}
      saving={saving}
      canSave={Boolean(row) && value.note.trim().length <= 200}
      error={error ?? gone}
      submitLabel="Zeit sperren"
      onSubmit={() => onSubmit({ ...value, ...time, note: value.note.trim() || null })}
      onClose={onClose}
    >
      <ResourcePickField
        rows={rows}
        value={value.resourceId}
        onChange={(next) =>
          patch({
            resourceId: next.resourceId,
            ...fitWindow(time.startMinute, time.endMinute, next),
          })
        }
      />
      <FormField label="Tag" required>
        <Select value={value.date} onChange={(event) => patch({ date: event.target.value })}>
          {dates.map((day) => (
            <option key={day} value={day}>
              {sheetDay.format(new Date(`${day}T00:00:00Z`))}
            </option>
          ))}
        </Select>
      </FormField>
      <ResourceTimeFields
        hours={row ?? null}
        startMinute={time.startMinute}
        endMinute={time.endMinute}
        onStart={(startMinute) => patch({ startMinute, endMinute: time.endMinute })}
        onEnd={(endMinute) => patch({ startMinute: time.startMinute, endMinute })}
      />
      <FormField label="Grund" required>
        <Select
          value={value.reason}
          onChange={(event) => patch({ reason: event.target.value as ResourceBlockReason })}
        >
          {(Object.keys(BLOCK_REASON_LABEL) as ResourceBlockReason[]).map((reason) => (
            <option key={reason} value={reason}>
              {BLOCK_REASON_LABEL[reason]}
            </option>
          ))}
        </Select>
      </FormField>
      <FormField label="Notiz (intern)" hint="Nur für den Betrieb sichtbar.">
        <Input
          value={value.note}
          maxLength={200}
          onChange={(event) => patch({ note: event.target.value })}
        />
      </FormField>
    </FormSheet>
  );
}
